function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { forwardRef, useContext, useMemo } from 'react';
import { EuiThemeContext, EuiModificationsContext, EuiColorModeContext, defaultComputedTheme } from './context';
import { getEuiDevProviderWarning } from './provider';
import { jsx as ___EmotionJSX } from "@emotion/react";
var providerMessage = "`EuiProvider` is missing which can result in negative effects.\nWrap your component in `EuiProvider`: https://ela.st/euiprovider.";
export var useEuiTheme = function useEuiTheme() {
  var theme = useContext(EuiThemeContext);
  var colorMode = useContext(EuiColorModeContext);
  var modifications = useContext(EuiModificationsContext);

  if (process.env.NODE_ENV !== 'production') {
    var isFallback = theme === defaultComputedTheme;
    var warningLevel = getEuiDevProviderWarning();

    if (isFallback && typeof warningLevel !== 'undefined') {
      switch (warningLevel) {
        case 'log':
          console.log(providerMessage);
          break;

        case 'warn':
          console.warn(providerMessage);
          break;

        case 'error':
          throw new Error(providerMessage);

        default:
          break;
      }
    }
  }

  var assembledTheme = useMemo(function () {
    return {
      euiTheme: theme,
      colorMode: colorMode,
      modifications: modifications
    };
  }, [theme, colorMode, modifications]);
  return assembledTheme;
};
// Provide the component props interface as the generic to allow the docs props table to populate.
// e.g., `const EuiComponent = withEuiTheme<EuiComponentProps>(_EuiComponent)`
export var withEuiTheme = function withEuiTheme(Component) {
  var componentName = Component.displayName || Component.name || 'ComponentWithTheme';

  var Render = function Render(props, ref) {
    var theme = useEuiTheme();
    return ___EmotionJSX(Component, _extends({
      theme: theme,
      ref: ref
    }, props));
  };

  var WithEuiTheme = /*#__PURE__*/forwardRef(Render);
  WithEuiTheme.displayName = componentName;
  return WithEuiTheme;
};